import { AdvancedSelect } from '../../../widgets/select';
import { Checkbox } from '../../../widgets/checkbox';
import { FormControl, SectionControl } from '../../../widgets/formControl';
import { showInfoNotification } from '../../../widgets/notificationToaster';
import { TextButton } from '../../../widgets/buttons';
import React, { FC, useEffect, useRef, useState } from 'react';
import { T } from 'src/shared/helpers';
import styled from 'styled-components';
import code128Preview from './images/code-128.png';
import qrcodePreview from './images/qrcode.png';
import { _NoEdgeInput, Placeholder, PlaceholderButtons, QrBarCodeType, QrCodeBarcodeMenuProps } from './sharedPlacementComponents';

const FooterContainer = styled.div`
    display: flex;
    flex-direction: row-reverse;
    margin-top: 20px;
`;

const codeType = [
    { value: 'qr', label: T._('QR Code', 'Admin'), preview: qrcodePreview },
    { value: 'code-128', label: 'Barcode 128', preview: code128Preview }
] as const;

const QrCodeBarcodeMenu: FC<QrCodeBarcodeMenuProps> = ({ onConfirm }) => {
    const [type, setType] = useState<{ value: QrBarCodeType; label: string; preview: string }>();
    const [data, setData] = useState<string>('');
    const [openPlaceholder, setOpenPlaceholder] = useState<boolean>(false);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
            // inputRef.current.setSelectionRange(data.length, data.length);
        }
    }, [inputRef, data]);

    const containsPlaceholder = () => Object.values(Placeholder).some((s) => data.includes(s));

    const handleConfirm = () => {
        if (!type?.value || data.length === 0) {
            showInfoNotification({ message: T._('Please, fill the fields', 'Admin') });
            return;
        }
        onConfirm(type!.value, data, containsPlaceholder());
    };

    return (
        <>
            <SectionControl label={T._('Code Type', 'Admin')}>
                <AdvancedSelect
                    isSearchable={false}
                    options={codeType}
                    placeholder={T._('Select code type...', 'Admin')}
                    onChange={(e) => {
                        const unit = e as (typeof codeType)[number];
                        setType(unit);
                    }}
                    isMulti={false}
                    styles={{
                        menu: (prev) => ({
                            ...prev,
                            width: 'auto',
                            zIndex: 3
                        }),
                        control: (provided, state) => ({
                            ...provided,
                            minWidth: '80px',
                            minHeight: '30px',
                            border: 'none',
                            borderRadius: '0',
                            borderBottom: '1px solid rgb(128, 128, 128)',
                            boxShadow: 'none',
                            '&:hover': {
                                border: 'none',
                                borderRadius: '0',
                                borderBottom: '1px solid rgb(128, 128, 128)'
                            },
                            ...(state.menuIsOpen && {
                                borderColor: 'transparent',
                                boxShadow: 'none'
                            })
                        }),
                        indicatorSeparator: () => ({
                            display: 'none'
                        }),
                        singleValue: (provided) => ({
                            ...provided,
                            padding: '0px 2px'
                        }),
                        dropdownIndicator: (provided) => ({
                            ...provided,
                            padding: 0
                        })
                    }}
                    value={type}
                />
            </SectionControl>

            <FormControl>
                <_NoEdgeInput
                    ref={inputRef}
                    value={data}
                    placeholder={T._('Insert here your data', 'Admin')}
                    type='text'
                    onChange={(e) => setData(e.currentTarget.value)}
                />
            </FormControl>

            <FormControl>
                <Checkbox label={T._('Show placeholders', 'Admin')} checked={openPlaceholder} onChange={() => setOpenPlaceholder(!openPlaceholder)} />
                {openPlaceholder && <PlaceholderButtons onButtonClick={(value) => setData(data + ' ' + value)} />}
            </FormControl>

            <FooterContainer>
                <TextButton onClick={handleConfirm}>{T._('Confirm', 'Admin')}</TextButton>
            </FooterContainer>
        </>
    );
};

export default QrCodeBarcodeMenu;
